<template>
  <div v-if="!authStore.auth">
    <div class="flex flex-col gap-2 h-full justify-center items-center">
      <div class="mb-2">
        {{ $t('profile.login_to_continue') }}
      </div>
      <BaseButton
        theme="primary"
        look="border"
        @click="authStore.openLoginModal"
      >
        {{ $t('header.login') }}
      </BaseButton>
    </div>
  </div>
  <div v-else-if="!companiesStore.company" class="py-10 flex justify-center">
    <BaseSpinner />
  </div>
  <div v-else class="space-y-3">
    <div>
      <div
        ref="profile"
        class="rounded-t-xl overflow-hidden bg-primary-300 w-full relative"
        :class="!companiesStore.company.cover && 'animated-background'"
        :style="{ height: '200px' }"
      >
        <img
          v-if="companiesStore.company.cover?.url"
          loading="lazy"
          :src="companiesStore.company.cover.url"
          class="w-full h-full object-cover"
        />
        <div class="absolute top-3 right-3">
          <FavoritesButton
            ref="favoriteButton"
            :entity-title="companiesStore.company.name || ''"
            :entity-id="companiesStore.company.id"
            :entity-type="EntityType.Company"
            button-class="text-primary-900 bg-primary-100 hover:text-primary-900 hover:bg-primary-200 active:bg-primary-200 h-8 px-4"
          />
        </div>
      </div>
      <div class="bg-white rounded-b-xl w-full p-4 sm:p-8 relative z-10">
        <div class="flex sm:justify-between justify-center">
          <div class="-mt-44 relative">
            <UsersUserAvatar
              :src="companiesStore.company.logo?.url"
              :abbr="companiesStore.company.name.slice(0, 2)"
              size="5xl"
              wrapper-class="shadow-md"
            />
          </div>
          <!-- <div v-if="usersStore.isMe" class="absolute sm:static right-4">
            <EditButton @click="onEdit">
              {{ $t('profile.edit_profile') }}
            </EditButton>
          </div> -->
        </div>
        <div class="font-bold text-2xl my-3">
          {{ companiesStore.company.name }}
        </div>
        <div v-if="companiesStore.company.country" class="flex gap-2 text-sm">
          <div>{{ companiesStore.company.country.name }}</div>
        </div>
      </div>
    </div>
    <div class="bg-white rounded-xl p-4 sm:p-8 flex flex-col gap-4 text-sm">
      <div class="text-xl font-bold">
        {{ $t('companies.about') }}
      </div>

      <div
        v-if="companiesStore.company.description"
        class="mb-4 whitespace-pre-wrap"
      >
        {{ companiesStore.company.description }}
      </div>

      <div v-if="companiesStore.company.site" class="flex gap-2">
        <div class="w-2/6">
          {{ $t('companies.website') }}
        </div>
        <div class="w-4/6">
          <BaseLink
            :href="companiesStore.company.site"
            target="_blank"
            class="!font-normal"
          >
            {{ companiesStore.company.site }}
          </BaseLink>
        </div>
      </div>

      <hr class="my-2" />

      <div v-if="companiesStore.company.size" class="flex gap-2">
        <div class="w-2/6">
          {{ $t('companies.employees_size') }}
        </div>
        <div class="w-4/6">{{ companySizeLabel }}</div>
      </div>

      <div v-if="companiesStore.company.business.length" class="flex gap-2">
        <div class="w-2/6 text-sm">{{ $t('companies.primary_business') }}</div>
        <div class="w-4/6 flex flex-wrap gap-2">
          <template
            v-for="item in companiesStore.company.business"
            :key="item.id"
          >
            <BaseBadge theme="info">
              {{ 'name' in item ? item.name : '' }}
            </BaseBadge>
          </template>
        </div>
      </div>

      <div v-if="companiesStore.company.tags.length" class="flex gap-2">
        <div class="w-2/6 text-sm">{{ $t('companies.tags') }}</div>
        <div class="w-4/6 flex flex-wrap gap-2">
          <template v-for="item in companiesStore.company.tags" :key="item">
            <BaseBadge theme="info">
              {{ getCompanyTagLabel(item) }}
            </BaseBadge>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuth } from '~/stores/auth'
import { EntityType } from '~/models/common'
import type { FavoritesButton } from '#build/components'
import { useDictionaries } from '~/stores/dictionaries'

const dictStore = useDictionaries()
const catalogs = useCatalog()
const companiesStore = useCompanies()
const authStore = useAuth()
const favoriteButton = ref<null | InstanceType<typeof FavoritesButton>>(null)

const companySizeLabel = computed(() => {
  return catalogs.companySizes.find(
    (item) => item.value === companiesStore.company?.size
  )?.label
})

const getCompanyTagLabel = (id: number) => {
  return dictStore.company.tag?.find((item) => item.value === id)?.label
}

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
})

onServerPrefetch(async () => {
  await load()
})

const load = async () => {
  if (authStore.auth) {
    await companiesStore.fetchCompany(props.id)
    await dictStore.fetchDictionaries()
  }
}

onMounted(async () => {
  await load()
})

watch(
  () => authStore.auth,
  async () => {
    await load()
  }
)
</script>
